import React, { useState, useEffect } from 'react';
import { Button, TextField, Grid, Typography, Checkbox, FormControlLabel, Select, MenuItem, SelectChangeEvent } from '@mui/material';
import { styled } from '@mui/material/styles';
import axios from '../../utils/axios.utils';

const Placeholder = styled('em')(({ theme }) => ({
  color: theme.palette.text.disabled,
  fontStyle: 'italic',
}));

const NewMfrLiquidForm = ({ onSuccess, showSnackbar = (message: string, severity: 'success' | 'error') => {} }: { onSuccess: () => void, showSnackbar?: (message: string, severity: 'success' | 'error') => void }) => {

  const [formValues, setFormValues] = useState({
    global_mfr_id: '',
    liquid_name: '',
    liquid_name_desc: '',
    mfr_product_id: '',
    liquid_type_id: '',
    epa_number: '',
    liquid_density: '',
    min_rate: '',
    max_rate: '',
    target_rate: '',
    liquid_density_tol: '',
    adjustable: false,
    adjust_by: '',
    locked: false,
    active: true,
  });

  const [liquidTypes, setLiquidTypes] = useState([]);
  const [globalMfrs, setGlobalMfrs] = useState([]);

  useEffect(() => {
    const fetchDropdowns = async () => {
      try {
        const [typesRes, mfrsRes] = await Promise.all([
          axios.get('/api/admin/autotreat_mfr/liquid_types/', {
            headers: { Authorization: `Bearer ${sessionStorage.getItem('accessToken')}` },
          }),
          axios.get('/api/admin/autotreat_mfr/mfrs/', {
            headers: { Authorization: `Bearer ${sessionStorage.getItem('accessToken')}` },
          }),
        ]);
        setLiquidTypes(typesRes.data);
        setGlobalMfrs(mfrsRes.data);
      } catch (err) {
        console.error('Error loading dropdown data:', err);
      }
    };

    fetchDropdowns();
  }, []);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<unknown>) => {
    const target = event.target as any; // Using 'as any' for pragmatic type handling across MUI components
    const name = target.name as string;
    let valueToSet: any;

    if (target.type === 'checkbox' && typeof target.checked === 'boolean') {
        valueToSet = target.checked;
    } else {
        valueToSet = target.value;
    }

    if (name) {
        setFormValues(prev => ({ ...prev, [name]: valueToSet }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await axios.post('/api/admin/autotreat_mfr/mfr_liquids/', formValues, {
        headers: { 
          Authorization: `Bearer ${sessionStorage.getItem('accessToken')}`,
          'Content-Type': 'application/json',
        },
      });
      
      showSnackbar('Treatment product created successfully!', 'success');
      if (onSuccess) onSuccess();
    } catch (error: any) {
      console.error('Error creating liquid:', error);
      const errorMessage = error?.response?.data?.message || 'Failed to create treatment product. Please try again.';
      showSnackbar(errorMessage, 'error');
    }
  };

  return (
    <form id="dialog-form" onSubmit={handleSubmit}>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <Select
            name="global_mfr_id"
            value={formValues.global_mfr_id}
            onChange={handleInputChange}
            fullWidth
            variant="outlined"
            label="Manufacturer"
            displayEmpty
            renderValue={(selected) => {
              if (!selected) return <Placeholder>Select Manufacturer</Placeholder>;
              const selectedMfr = globalMfrs.find(mfr => mfr.global_mfr_id === selected);
              return selectedMfr?.mfr_name || selected;
            }}
          >
            <MenuItem value="" disabled>
              <Placeholder>Select Manufacturer</Placeholder>
            </MenuItem>
            {globalMfrs.map((mfr) => (
              <MenuItem key={mfr.global_mfr_id} value={mfr.global_mfr_id}>
                {mfr.mfr_name}
              </MenuItem>
            ))}
          </Select>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Select
            name="liquid_type_id"
            value={formValues.liquid_type_id}
            onChange={handleInputChange}
            fullWidth
            variant="outlined"
            label="Liquid Type"
            displayEmpty
            renderValue={(selected) => {
              if (!selected) return <Placeholder>Select Liquid Type</Placeholder>;
              const selectedType = liquidTypes.find(type => type.liquid_type_id === selected);
              return selectedType?.liquid_type || selected;
            }}
          >
            <MenuItem value="" disabled>
              <Placeholder>Select Liquid Type</Placeholder>
            </MenuItem>
            {liquidTypes.map((type) => (
              <MenuItem key={type.liquid_type_id} value={type.liquid_type_id}>
                {type.liquid_type}
              </MenuItem>
            ))}
          </Select>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            name="liquid_name"
            label="Liquid Name"
            fullWidth
            variant="outlined"
            value={formValues.liquid_name}
            onChange={handleInputChange}
            required
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            name="liquid_name_desc"
            label="Liquid Description"
            fullWidth
            variant="outlined"
            value={formValues.liquid_name_desc}
            onChange={handleInputChange}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            name="epa_number"
            label="EPA Number"
            fullWidth
            variant="outlined"
            value={formValues.epa_number}
            onChange={handleInputChange}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            name="mfr_product_id"
            label="MFR Product ID"
            fullWidth
            variant="outlined"
            value={formValues.mfr_product_id}
            onChange={handleInputChange}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            type="number"
            name="liquid_density"
            label="Liquid Density"
            fullWidth
            variant="outlined"
            value={formValues.liquid_density}
            onChange={handleInputChange}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            type="number"
            name="min_rate"
            label="Min Rate"
            fullWidth
            variant="outlined"
            value={formValues.min_rate}
            onChange={handleInputChange}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            type="number"
            name="max_rate"
            label="Max Rate"
            fullWidth
            variant="outlined"
            value={formValues.max_rate}
            onChange={handleInputChange}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            type="number"
            name="target_rate"
            label="Target Rate"
            fullWidth
            variant="outlined"
            value={formValues.target_rate}
            onChange={handleInputChange}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            type="number"
            name="liquid_density_tol"
            label="Liquid Density TOL"
            fullWidth
            variant="outlined"
            value={formValues.liquid_density_tol}
            onChange={handleInputChange}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControlLabel
            control={<Checkbox name="adjustable" checked={formValues.adjustable} onChange={handleInputChange} />}
            label="Adjustable"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            type="number"
            name="adjust_by"
            label="Adjust By"
            fullWidth
            variant="outlined"
            value={formValues.adjust_by}
            onChange={handleInputChange}
            disabled={!formValues.adjustable}
            InputLabelProps={{
              shrink: formValues.adjustable || formValues.adjust_by ? true : undefined,
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControlLabel
            control={<Checkbox name="locked" checked={formValues.locked} onChange={handleInputChange} />}
            label="Locked"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControlLabel
            control={<Checkbox name="active" checked={formValues.active} onChange={handleInputChange} />}
            label="Active"
          />
        </Grid>
      </Grid>
    </form>
  );
};

export default NewMfrLiquidForm;